import { useState, useCallback, ReactNode, useEffect, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users,
  Settings,
  LogOut,
  GraduationCap,
  DollarSign,
  Menu,
  X,
  UsersRound,
  ArrowLeft,
  BarChart3,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { usePartnerRole } from "@/hooks/usePartnerRole";
import { usePartnerOrgs } from "@/hooks/usePartnerOrgs";
import { usePartnerPermissions, PartnerPermissionKey } from "@/hooks/usePartnerPermissions";
import { useMaintenanceMode } from "@/hooks/useMaintenanceMode";
import { ThemeToggle } from "@/components/ThemeToggle";
import { DataProtectionProvider } from "@/components/DataProtectionProvider";
import { MaintenanceBanner } from "@/components/MaintenanceBanner";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

interface PartnerLayoutProps {
  children: ReactNode;
}

interface PartnerBranding {
  logo_light_url?: string | null;
  logo_dark_url?: string | null;
  logo_scale?: number;
  logo_position_x?: number;
  logo_position_y?: number;
}

interface NavItem {
  to: string;
  icon: React.ElementType;
  label: string;
  end?: boolean;
  badge?: string;
  permission?: PartnerPermissionKey;
}

export function PartnerLayout({ children }: PartnerLayoutProps) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { partnerData } = usePartnerRole();
  const permissions = usePartnerPermissions();
  const { orgs } = usePartnerOrgs();
  const { toast } = useToast();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Consumir welcome message do sessionStorage (exibir apenas uma vez por login)
  useEffect(() => {
    const msg = sessionStorage.getItem('partner_welcome_message');
    if (msg) {
      sessionStorage.removeItem('partner_welcome_message');
      toast({ title: msg });
    }
  }, []);

  // Detect theme changes
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains("dark"));
    };
    checkDarkMode();
    
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    
    return () => observer.disconnect();
  }, []);

  const hasOrgs = orgs && orgs.length > 0;
  
  // Get branding settings
  const branding: PartnerBranding = (partnerData as any)?.branding || {};
  
  // Choose logo based on current theme
  const getThemeLogo = () => {
    if (isDarkMode) {
      return branding.logo_dark_url || branding.logo_light_url || partnerData?.logo_url;
    }
    return branding.logo_light_url || partnerData?.logo_url;
  };
  
  const currentLogo = getThemeLogo();
  
  const logoStyle = {
    transform: `scale(${(branding.logo_scale || 100) / 100})`,
    objectPosition: `${branding.logo_position_x || 50}% ${branding.logo_position_y || 50}%`,
  };

  const handleLogout = useCallback(async () => {
    try {
      await signOut();
    } catch (e) {
      console.error("Erro durante logout:", e);
    } finally {
      // Sempre navegar para /auth, independente do resultado
      navigate("/auth", { replace: true });
    }
  }, [signOut, navigate]);

  // Navigation items with permission-based filtering
  const allNavItems: NavItem[] = useMemo(() => [
    { to: "/partner", icon: LayoutDashboard, label: "Dashboard", end: true, permission: "canViewDashboard" },
    { to: "/partner/receita-clientes", icon: DollarSign, label: "Receita & Clientes", permission: "canViewReceitaClientes" },
    { to: "/partner/team-payments", icon: Wallet, label: "Pagamento de Equipe", permission: "canViewTeamPayments" },
    { to: "/partner/ads", icon: BarChart3, label: "Relatórios Ads", badge: "Em construção", permission: "canViewReports" },
    { to: "/partner/orgs", icon: Building2, label: "Organizações", permission: "canViewOrgs" },
    { to: "/partner/team", icon: UsersRound, label: "Equipe", permission: "canViewTeam" },
    { to: "/partner/courses", icon: GraduationCap, label: "Cursos", badge: "Em construção", permission: "canViewCourses" },
    { to: "/partner/settings", icon: Settings, label: "Configurações", permission: "canViewSettings" },
  ], []);

  // Filter nav items based on permissions
  const navItems = useMemo(() => 
    allNavItems.filter(item => !item.permission || permissions[item.permission]),
    [allNavItems, permissions]
  );

  const { isMaintenanceMode } = useMaintenanceMode();

  // Resolver footer_text do parceiro (branding completo do partnerData)
  const footerText = ((partnerData as any)?.branding as any)?.footer_text as string | undefined;

  return (
    <DataProtectionProvider mode="strict" allowCopyInInputs={true}>
    <MaintenanceBanner />
    <div className={cn("min-h-screen flex bg-background", isMaintenanceMode && "pt-11")}>
      {/* Toggle Button - Fixed position, always visible */}
      <Button
        variant="outline"
        size="icon"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className={cn(
          "fixed top-3 z-50 h-9 w-9 shadow-md transition-all duration-300",
          sidebarOpen ? "left-[17rem]" : "left-3"
        )}
      >
        {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </Button>

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-40 h-screen bg-sidebar border-r border-sidebar-border flex flex-col transition-all duration-300",
          sidebarOpen ? "w-64 translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-sidebar-border">
          <div className="flex items-center gap-2">
            {currentLogo ? (
              <div className="h-8 w-8 rounded-lg overflow-hidden flex items-center justify-center bg-background shrink-0">
                <img
                  src={currentLogo}
                  alt={partnerData?.name}
                  className="max-h-full max-w-full object-contain"
                  style={logoStyle}
                />
              </div>
            ) : (
              <div
                className="h-8 w-8 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0"
                style={{ backgroundColor: partnerData?.primary_color || "#3b82f6" }}
              >
                {partnerData?.name?.charAt(0)?.toUpperCase() || "P"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-sidebar-foreground truncate block">
                {partnerData?.name || "Partner Portal"}
              </span>
              <span className="text-xs text-muted-foreground capitalize">
                {partnerData?.type}
              </span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 p-4 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors whitespace-nowrap",
                  isActive
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"
                )
              }
            >
              <item.icon className="h-5 w-5 shrink-0" />
              <span className="flex items-center gap-2">
                {item.label}
                {"badge" in item && item.badge && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-amber-500/10 text-amber-600 border-amber-500/30">
                    {item.badge}
                  </Badge>
                )}
              </span>
            </NavLink>
          ))}

          <div className="my-4 border-t border-sidebar-border" />

          {hasOrgs && (
            <NavLink
              to="/dashboard"
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-sidebar-accent/50 transition-colors whitespace-nowrap"
            >
              <ArrowLeft className="h-5 w-5 shrink-0" />
              Acessar Cliente
            </NavLink>
          )}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-sidebar-border space-y-2">
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              variant="outline"
              className="flex-1 justify-start gap-3"
              onClick={handleLogout}
            >
              <LogOut className="h-5 w-5" />
              Sair
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={cn(
          "flex-1 overflow-x-hidden transition-all duration-300 pt-14 flex flex-col",
          sidebarOpen ? "ml-64" : "ml-0"
        )}
      >
        <div className="flex-1 container mx-auto py-6 px-6">
          {children}
        </div>
        {footerText && (
          <footer className="border-t px-4 py-3 text-center text-xs text-muted-foreground">
            {footerText}
          </footer>
        )}
      </main>
    </div>
    </DataProtectionProvider>
  );
}
